'use client';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface PaginationSectionProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export default function PaginationSection({
  currentPage,
  totalPages,
  onPageChange,
}: PaginationSectionProps) {
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
      window.scrollTo({ top: 400, behavior: 'smooth' });
    }
  };

  if (totalPages <= 1) return null;

  return (
    <section className="bg-white py-8 sm:py-12 px-4 sm:px-6 border-t border-gray-200">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`min-h-[44px] flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium text-sm transition-all active:scale-[0.98] ${
              currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-navy border border-navy hover:bg-navy hover:text-white'
            }`}
          >
            <ChevronLeftIcon className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Previous</span>
          </button>

          <div className="flex items-center justify-center gap-1.5 sm:gap-2 flex-wrap">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`min-w-[44px] min-h-[44px] w-10 h-10 rounded-lg font-medium text-sm transition-all active:scale-[0.98] ${
                  currentPage === page
                    ? 'bg-gold text-white shadow-md'
                    : 'bg-white text-navy border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {page}
              </button>
            ))}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`min-h-[44px] flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg font-medium text-sm transition-all active:scale-[0.98] ${
              currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-navy border border-navy hover:bg-navy hover:text-white'
            }`}
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRightIcon className="w-4 h-4 flex-shrink-0" />
          </button>
        </div>

        <p className="text-center text-gray-500 text-xs sm:text-sm mt-3 sm:mt-4">
          Page {currentPage} of {totalPages}
        </p>
      </div>
    </section>
  );
}
